import api from './api';

// ===================== Types =====================

export interface RevenueChannel {
  id: number;
  name: string;
  type?: string;
  channelType?: string;
  commissionPercent: number;
  settlementDays?: number;
  color?: string;
  isDefault?: boolean;
  isActive?: boolean;
  displayOrder?: number;
  active?: boolean;
  /**
   * Controls when revenue from this channel is recorded as IncomeEntry rows.
   *  - true  → income recorded immediately at EOD (Cash/Card/UPI default).
   *  - false → income only recorded when an aggregator settlement is saved
   *            (matches actual bank payout). Recommended for ONLINE_AGGREGATOR.
   *
   * If omitted on create, backend computes default by channelType
   * (ONLINE_AGGREGATOR=false, else=true).
   */
  recordRevenueAsIncome: boolean;
}

export interface DailyRevenueEntry {
  channelId: number;
  channelName?: string;
  grossAmount: number;
  commissionAmount: number;
  netAmount: number;
}

export interface DailyRevenueRequest {
  date: string;
  entries: DailyRevenueEntry[];
}

export interface DailyRevenueEntryResponse {
  id: number;
  channelId: number;
  channelName?: string;
  channelType?: string;
  date?: string;
  grossAmount: number;
  commissionAmount: number;
  netAmount: number;
  settlementDate?: string;
  isSettled?: boolean;
}

export interface DailyRevenueResponse {
  date: string;
  entries: (DailyRevenueEntry & { id: number })[];
  totalGross: number;
  totalCommission: number;
  totalNet: number;
}

export interface PaymentMode {
  id: number;
  name: string;          // canonical, UPPERCASE (e.g. "CASH")
  displayName: string;   // human label (e.g. "Cash")
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

// ===================== API =====================

export const financeApi = {
  // ---- Revenue Channels ----
  getChannels: (params?: Record<string, unknown>) =>
    api.get<RevenueChannel[]>('/api/vendor/finance/channels', { params }),

  createChannel: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/channels', data),

  updateChannel: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/channels/${id}`, data),

  deleteChannel: (id: number) =>
    api.delete(`/api/vendor/finance/channels/${id}`),

  submitDailyRevenue: (data: DailyRevenueRequest) =>
    api.post<DailyRevenueResponse>('/api/vendor/finance/channels/daily', data),

  getDailyRevenue: (date: string) =>
    api.get<DailyRevenueEntryResponse[]>('/api/vendor/finance/channels/daily', {
      params: { dateFrom: date, dateTo: date },
    }),

  getDailyRevenueRange: (dateFrom: string, dateTo: string) =>
    api.get<DailyRevenueEntryResponse[]>('/api/vendor/finance/channels/daily', {
      params: { dateFrom, dateTo },
    }),

  // Note: no backend /seed endpoint exists. Channels are created via
  // createChannel or vendor setup. This is kept as a no-op fallback.
  seedDefaultChannels: () =>
    Promise.resolve({ data: [] as RevenueChannel[] }),

  // ---- Expenses ----
  getExpenses: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/expenses', { params }),

  getExpense: (id: number) =>
    api.get(`/api/vendor/finance/expenses/${id}`),

  createExpense: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/expenses', data),

  updateExpense: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/expenses/${id}`, data),

  deleteExpense: (id: number) =>
    api.delete(`/api/vendor/finance/expenses/${id}`),

  bulkUpdateExpenseCategory: (supplierName: string, categoryId: number) =>
    api.post('/api/vendor/finance/expenses/bulk-update-category', { supplierPattern: supplierName, newCategoryId: categoryId }),

  getUniqueSuppliers: () =>
    api.get<string[]>('/api/vendor/finance/expenses/suppliers'),

  // ---- Expense Categories ----
  getCategories: () =>
    api.get('/api/vendor/finance/categories'),

  createCategory: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/categories', data),

  updateCategory: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/categories/${id}`, data),

  deleteCategory: (id: number) =>
    api.delete(`/api/vendor/finance/categories/${id}`),

  updateCategoryBudget: (id: number, monthlyBudget: number) =>
    api.put(`/api/vendor/finance/categories/${id}/budget`, { monthlyBudget }),

  // ---- Income ----
  getIncome: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/income', { params }),

  getIncomeSummary: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/income/summary', { params }),

  createIncome: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/income', data),

  updateIncome: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/income/${id}`, data),

  deleteIncome: (id: number) =>
    api.delete(`/api/vendor/finance/income/${id}`),

  bulkUpdateIncomeCategory: (source: string, category: string) =>
    api.post('/api/vendor/finance/income/bulk-update-category', { source, category }),

  // ---- Income Categories ----
  getIncomeCategories: () =>
    api.get('/api/vendor/finance/income-categories'),

  createIncomeCategory: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/income-categories', data),

  updateIncomeCategory: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/income-categories/${id}`, data),

  deleteIncomeCategory: (id: number) =>
    api.delete(`/api/vendor/finance/income-categories/${id}`),

  // ---- Dashboard ----
  getDashboard: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/dashboard', { params }),

  // ---- Export ----
  exportData: (section: string, params?: Record<string, unknown>) =>
    api.get(`/api/vendor/finance/export/${section}`, { params, responseType: 'blob' }),

  // ---- Recurring Expenses ----
  getRecurringTemplates: () =>
    api.get('/api/vendor/finance/recurring'),

  createRecurringTemplate: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/recurring', data),

  pauseRecurringTemplate: (id: number) =>
    api.post(`/api/vendor/finance/recurring/${id}/pause`),

  resumeRecurringTemplate: (id: number) =>
    api.post(`/api/vendor/finance/recurring/${id}/resume`),

  cancelRecurringTemplate: (id: number) =>
    api.post(`/api/vendor/finance/recurring/${id}/cancel`),

  // ---- Alerts ----
  getAlerts: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/alerts', { params }),

  getUnreadAlertCount: () =>
    api.get<number>('/api/vendor/finance/alerts/unread-count'),

  markAlertRead: (id: number) =>
    api.post(`/api/vendor/finance/alerts/${id}/read`),

  markAllAlertsRead: () =>
    api.post('/api/vendor/finance/alerts/read-all'),

  // ---- Payment Modes ----
  getPaymentModes: () =>
    api.get<PaymentMode[]>('/api/vendor/finance/payment-modes'),

  createPaymentMode: (data: { name: string; displayName?: string }) =>
    api.post<PaymentMode>('/api/vendor/finance/payment-modes', data),

  updatePaymentMode: (
    id: number,
    data: { displayName?: string; isActive?: boolean; sortOrder?: number },
  ) => api.put<PaymentMode>(`/api/vendor/finance/payment-modes/${id}`, data),

  deletePaymentMode: (id: number) =>
    api.delete(`/api/vendor/finance/payment-modes/${id}`),

  // ---- Settings ----
  getSettings: () =>
    api.get('/api/vendor/finance/settings'),

  updateSettings: (data: Record<string, unknown>) =>
    api.put('/api/vendor/finance/settings', data),

  // ---- Non-Revenue Costs ----
  getNonRevenueCosts: (params?: Record<string, unknown>) =>
    api.get('/api/vendor/finance/non-revenue-costs', { params }),

  createNonRevenueCost: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/non-revenue-costs', data),

  // ---- Payables ----
  getPayables: (status: 'outstanding' | 'completed' | 'all' = 'outstanding') =>
    api.get('/api/vendor/finance/payables', { params: { status } }),

  recordPayment: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/payables/pay', data),

  recordBulkPayment: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/payables/pay-bulk', data),

  updateSupplierPayment: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/vendor/finance/payables/payments/${id}`, data),

  deleteSupplierPayment: (id: number) =>
    api.delete(`/api/vendor/finance/payables/payments/${id}`),

  getSupplierPaymentHistory: (params: {
    supplier: string;
    page?: number;
    size?: number;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api.get('/api/vendor/finance/payables/supplier/history', { params }),

  // ---- Bank Import ----
  uploadBankStatement: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/vendor/finance/import/detect-columns', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  previewImport: (
    fileHash: string,
    columnMappings: Record<string, string>,
    dateFormat: string,
    statusFilter?: string,
    applyVat?: boolean,
  ) =>
    api.post('/api/vendor/finance/import/preview', {
      fileHash,
      columnMappings,
      dateFormat,
      statusFilter,
      applyVat,
    }),

  confirmBankImport: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/import/confirm', data),

  getImportHistory: () =>
    api.get('/api/vendor/finance/import/history'),

  // ---- CSV Templates ----
  getCsvTemplates: () =>
    api.get('/api/vendor/finance/import/templates'),

  saveCsvTemplate: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/import/templates', data),

  deleteCsvTemplate: (id: number) =>
    api.delete(`/api/vendor/finance/import/templates/${id}`),

  // ---- Transaction Rules ----
  getTransactionRules: () =>
    api.get('/api/vendor/finance/import/rules'),

  createTransactionRule: (data: Record<string, unknown>) =>
    api.post('/api/vendor/finance/import/rules', data),

  deleteTransactionRule: (id: number) =>
    api.delete(`/api/vendor/finance/import/rules/${id}`),

  // ---- Apply Similar (auto-categorize matching rows) ----
  applySimilar: (data: {
    sourceCounterparty: string | null;
    sourceDescription: string;
    direction: 'EXPENSE' | 'INCOME';
    categoryId?: number | null;
    incomeCategory?: string | null;
    uncategorizedRows: Array<{ rowIndex: number; description: string; counterparty: string | null }>;
  }) =>
    api.post<{
      pattern: string;
      matchedRowIndices: number[];
      matchedCount: number;
      ruleCreated: boolean;
      categoryId: number | null;
      categoryName: string | null;
      incomeCategory: string | null;
    }>('/api/vendor/finance/import/apply-similar', data),

  // ---- Insights ----
  getInsights: () =>
    api.get<FinanceInsight[]>('/api/vendor/finance/insights'),

  getInsightCounts: () =>
    api.get<InsightCounts>('/api/vendor/finance/insights/counts'),

  dismissInsight: (id: number) =>
    api.post<{ dismissed: boolean }>(`/api/vendor/finance/insights/${id}/dismiss`),

  snoozeInsight: (id: number, days = 7) =>
    api.post<{ snoozed: boolean; days: number }>(
      `/api/vendor/finance/insights/${id}/snooze`,
      { days },
    ),

  actInsight: (id: number) =>
    api.post<{ acted: boolean }>(`/api/vendor/finance/insights/${id}/act`),

  refreshInsights: () =>
    api.post<{ refreshed: boolean }>('/api/vendor/finance/insights/refresh'),

  hideInsightType: (insightType: string, days = 30) =>
    api.post<{ hidden: boolean; type: string; days: number }>(
      '/api/vendor/finance/insights/hide-type',
      { insightType, days },
    ),
};

// ===================== Insights types =====================

export type InsightSeverity = 'CRITICAL' | 'WARN' | 'INFO';

export interface InsightEvidence {
  math?: Record<string, string | number | boolean | null>;
  thresholds?: Record<string, string | number | boolean | null>;
  rows?: Array<Record<string, string | number | boolean | null>>;
  period_this?: [string, string];
  period_prev?: [string, string];
  suggestions?: string[];
  detector?: string;
  confidence?: number;
}

export interface FinanceInsight {
  id: number;
  insightType: string;
  severity: InsightSeverity;
  title: string;
  body: string;
  impactAmount: number | null;
  impactPeriod: 'WEEKLY' | 'MONTHLY' | 'ONE_TIME' | null;
  confidence: number | null;
  evidence: InsightEvidence | null;
  actionType: string | null;
  actionPayload: Record<string, string | number | boolean | null> | null;
  detectedAt: string;
  periodStart: string | null;
  periodEnd: string | null;
  snoozedUntil: string | null;
  actedAt: string | null;
}

export interface InsightCounts {
  critical: number;
  warn: number;
  info: number;
  total: number;
}
